import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { toast } from "sonner";
import { scrollToId } from "@/lib/smooth-scroll";

gsap.registerPlugin(ScrollTrigger);

const LOGOS = [
  "OpenAI",
  "Google Cloud",
  "Anthropic",
  "WordPress",
  "Meta",
  "Emergent",
  "Higgsfield",
  "N8N",
  "Notion",
  "Hostinger",
  "Python",
];

export const CTASection = () => {
  const sectionRef = useRef(null);
  const headlineRef = useRef(null);
  const glowRef = useRef(null);
  const formRef = useRef(null);
  const marqueeRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = gsap.context(() => {
      if (!reduced) {
        gsap.fromTo(
          headlineRef.current,
          { scale: 0.8 },
          {
            scale: 1,
            ease: "none",
            scrollTrigger: {
              trigger: headlineRef.current,
              start: "top 95%",
              end: "top 35%",
              scrub: true,
            },
          }
        );

        // Golden glow blooms behind the headline
        gsap.fromTo(
          glowRef.current,
          { opacity: 0, scale: 0.7, xPercent: -50 },
          {
            opacity: 1,
            scale: 1,
            xPercent: -50,
            ease: "none",
            scrollTrigger: {
              trigger: headlineRef.current,
              start: "top 95%",
              end: "top 40%",
              scrub: true,
            },
          }
        );

        // Form rises in with scrub
        gsap.fromTo(
          formRef.current,
          { y: 90, opacity: 0.25 },
          {
            y: 0,
            opacity: 1,
            ease: "none",
            scrollTrigger: {
              trigger: formRef.current,
              start: "top 98%",
              end: "top 60%",
              scrub: true,
            },
          }
        );

        // Marquee speeds up with scroll velocity
        const loop = gsap.to(marqueeRef.current, {
          xPercent: -50,
          ease: "none",
          duration: 40,
          repeat: -1,
        });
        const proxy = { ts: 1 };
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          onUpdate: (self) => {
            const target = 1 + Math.min(Math.abs(self.getVelocity()) / 700, 4);
            if (target > proxy.ts) {
              proxy.ts = target;
              loop.timeScale(target);
              gsap.to(proxy, {
                ts: 1,
                duration: 1.6,
                ease: "power2.out",
                overwrite: true,
                onUpdate: () => loop.timeScale(proxy.ts),
              });
            }
          },
        });
      }
      gsap.fromTo(
        ".cta-fade",
        { opacity: 0, y: reduced ? 0 : 36 },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: { trigger: ".cta-fade-group", start: "top 82%" },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Honeypot: silently drop bot submissions
    if (e.target.botcheck && e.target.botcheck.checked) return;
    if (form.message.trim().length < 10) {
      toast.error("Bitte beschreiben Sie Ihr Anliegen mit mindestens 10 Zeichen.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: `Neue Kontaktanfrage von ${form.name} — DirektLab`,
          from_name: "DirektLab Webseite",
          name: form.name,
          email: form.email,
          company: form.company || "—",
          message: form.message,
          botcheck: false,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Web3Forms error");
      }
      toast.success("Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen.");
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      id="kontakt"
      data-section-idx="5"
      className="bg-greige relative overflow-hidden"
      data-testid="cta-section"
    >
      <div className="h-24 w-full bg-gradient-to-b from-[#1E1B18] to-transparent lg:h-32" />
      <div className="grain-overlay" />

      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div ref={glowRef} className="cta-glow" aria-hidden="true" />
        <div className="cta-fade-group mx-auto max-w-4xl text-center">
          <p className="cta-fade eyebrow mb-6 text-[#6E685F]">006 / 006 — Kontakt</p>
          <h2
            ref={headlineRef}
            className="font-display text-5xl text-[#2B2825] sm:text-6xl lg:text-7xl will-change-transform"
            data-testid="cta-headline"
          >
            Bereit, Zeit zurück&shy;zugewinnen?
          </h2>
          <p className="cta-fade mx-auto mt-7 max-w-xl text-base text-[#6E685F] md:text-lg">
            Schildern Sie uns Ihr Vorhaben in wenigen Sätzen. Wir antworten
            innerhalb von 24 Stunden mit einer ersten fachlichen Einschätzung
            und konkreten nächsten Schritten.
          </p>
          <div className="cta-fade mt-10 flex flex-wrap items-center justify-center gap-5">
            <button
              onClick={() => scrollToId("#kontakt-formular")}
              className="btn-primary"
              data-testid="cta-primary-button"
            >
              Kostenloses Erstgespräch
            </button>
            <button
              onClick={() => scrollToId("#leistungen")}
              className="btn-secondary"
              data-testid="cta-secondary-button"
            >
              Leistungen ansehen
            </button>
          </div>
        </div>

        <form
          id="kontakt-formular"
          ref={formRef}
          onSubmit={handleSubmit}
          className="neu-card mx-auto mt-20 max-w-2xl p-8 sm:p-12"
          data-testid="contact-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <input
              required
              type="text"
              minLength={2}
              placeholder="Ihr Name *"
              value={form.name}
              onChange={handleChange("name")}
              className="neu-input w-full px-5 py-4 text-sm"
              data-testid="contact-name-input"
            />
            <input
              required
              type="email"
              placeholder="Ihre E-Mail *"
              value={form.email}
              onChange={handleChange("email")}
              className="neu-input w-full px-5 py-4 text-sm"
              data-testid="contact-email-input"
            />
          </div>
          <input
            type="text"
            placeholder="Unternehmen / Praxis (optional)"
            value={form.company}
            onChange={handleChange("company")}
            className="neu-input mt-5 w-full px-5 py-4 text-sm"
            data-testid="contact-company-input"
          />
          <textarea
            required
            rows={5}
            minLength={10}
            placeholder="Ihr Anliegen *"
            value={form.message}
            onChange={handleChange("message")}
            className="neu-input mt-5 w-full resize-none px-5 py-4 text-sm"
            data-testid="contact-message-input"
          />
          <input
            type="checkbox"
            name="botcheck"
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary mt-8 w-full disabled:opacity-60"
            data-testid="contact-submit-button"
          >
            {submitting ? "Wird gesendet …" : "Nachricht senden"}
          </button>
          {sent && (
            <p
              className="font-serif-accent mt-6 text-center text-lg text-[#A8854B]"
              data-testid="contact-success-message"
            >
              Vielen Dank! Ihre Nachricht ist angekommen — wir melden uns
              innerhalb von 24 Stunden.
            </p>
          )}
        </form>
      </div>

      <div className="relative pb-24">
        <p className="eyebrow mb-8 text-center text-[#6E685F]/70">
          Werkzeuge & Partner, mit denen wir arbeiten
        </p>
        <div className="marquee-mask" data-testid="logo-marquee">
          <div ref={marqueeRef} className="marquee-track">
            {[...LOGOS, ...LOGOS].map((logo, i) => (
              <span key={i} className="marquee-item">
                {logo}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
